// Métadonnées SEO des pages « VRAM requirements » : une par modèle du calculateur.
// Lues par vite.config.ts (en-têtes du HTML prérendu), entry-server (prerender,
// sitemap, IndexNow) et la page elle-même (SeoHead à la navigation).
import { MODELS, modelSheet, type ModelPreset } from "../pages/llmVram.ts";
import { SITE, base, latestDay, seoTitle, type RouteSeo } from "./routes.ts";

export const modelSlug = (m: ModelPreset) => m.id.replace(/\./g, "-"); // pas de point : nginx/Caddy y voient une extension
export const modelPath = (m: ModelPreset) => `/vram-requirements/${modelSlug(m)}`;
export const modelName = (m: ModelPreset) => m.label.replace(/ \(MoE\)$/, "");
export const modelBySlug = (slug: string) => MODELS.find((m) => modelSlug(m) === slug);

/**
 * Dernière vraie modification d'une fiche, pour le sitemap : sa sortie, ou la dernière refonte du gabarit (ici,
 * FAQ RTX 5090, Mac et 128 Go du 16 octobre 2026). Pas le relevé des prix, qui change chaque heure : daté du jour
 * sur 120 fiches à chaque build, le lastmod ne disait plus rien à Google, qui l'ignorait (dont pour les nouveautés).
 * À avancer quand le contenu de toutes les fiches change vraiment.
 */
export const MODEL_PAGES_CHANGED = "2026-10-16";

const gib = (x: number) => (x >= 100 ? x.toFixed(0) : x >= 10 ? x.toFixed(1) : x.toFixed(2));

/** 160 caractères au plus (au-delà, Google coupe) : la fin se raccourcit pour les noms longs. */
export function modelDescription(name: string, bf16: string, q4: string, cache = "KV cache"): string {
  const head = `How much VRAM does ${name} need? ${bf16} GiB in BF16, about ${q4} GiB in 4-bit, plus ${cache}.`;
  const tail = [" GPUs needed and the cheapest setup to rent today.", " GPUs needed and the cheapest setup to rent.", " GPUs needed and cheapest setup."]
    .find((t) => head.length + t.length <= 160) ?? "";
  return head + tail;
}

export const VRAM_MODEL_SEO: Record<string, RouteSeo> = Object.fromEntries(
  MODELS.map((m) => {
    const sheet = modelSheet(m);
    const bf16 = sheet.formats.find((f) => f.id === "bf16")!.weightsGiB;
    const q4 = sheet.formats.find((f) => f.id === "q4_k_m")!.weightsGiB;
    const name = modelName(m);
    return [
      modelPath(m),
      {
        ...base,
        title: seoTitle(`${name} VRAM Requirements`, ": BF16, FP8 & 4-bit"),
        description: modelDescription(name, gib(bf16), gib(q4), m.kv.length ? "KV cache" : "a fixed state per request"),
        url: `${SITE}${modelPath(m)}`,
        imageAlt: `${name} VRAM requirements`,
        ogType: "article",
        lastmod: latestDay(MODEL_PAGES_CHANGED, m.released ?? MODEL_PAGES_CHANGED),
        priority: 0.7,
      },
    ];
  })
);
